import { type CSSProperties, memo, type ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SortDirection } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { SortButton } from '~/components/SortButton/SortButton';
import { TableCell } from '~/components/TableView/TableCell';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { setSearchSortOverrideAction, setSortingFieldAction } from '~/store/configViews/actions';
import { sortingConfigViewOptionsSelector } from '~/store/configViews/selectors';
import { getDefaultSortDirection } from '~/store/configViews/utils';

type SortableHeaderProps = {
  title: string;
  sortingType: string;
  className?: string;
  style?: CSSProperties;
  resizeHandle?: ReactNode;
};

export const SortableHeader = memo(({ title, sortingType, className, resizeHandle, ...props }: SortableHeaderProps) => {
  const dispatch = useDispatch();
  const sortingConfigViewOptions = useSelector(sortingConfigViewOptionsSelector);
  const sortingConfigViewOption = sortingConfigViewOptions.find((opt) => opt.fieldName === sortingType);
  const sortingConfigDirection = sortingConfigViewOption?.sortDirection ?? getDefaultSortDirection(sortingType);
  const { appliedSortFieldDirection, appliedSortFieldName } = useAppliedSortField();
  const isActive = appliedSortFieldName === sortingType;

  const onSortToggle = useCallback(() => {
    if (!isActive) {
      dispatch(setSortingFieldAction({ sortingFieldName: sortingType, direction: sortingConfigDirection }));
    } else if (appliedSortFieldDirection === SortDirection.desc) {
      dispatch(setSortingFieldAction({ sortingFieldName: sortingType, direction: SortDirection.asc }));
    } else {
      dispatch(setSortingFieldAction({ sortingFieldName: sortingType, direction: SortDirection.desc }));
    }

    dispatch(setSearchSortOverrideAction({ searchSortOverride: null }));
  }, [appliedSortFieldDirection, dispatch, isActive, sortingConfigDirection, sortingType]);

  if (!sortingConfigViewOption) {
    return null;
  }

  return (
    <TableCell className={cn('p-1.5 px-2', className, !!resizeHandle && 'relative')} {...props}>
      <SortButton isActive={isActive} direction={sortingConfigDirection} onClick={onSortToggle} type={title}>
        {title}
      </SortButton>
      {resizeHandle}
    </TableCell>
  );
});

SortableHeader.displayName = 'SortableHeader';
